import {faBowlHot} from '@fortawesome/pro-regular-svg-icons';
import {useRouter} from 'next/router';
import useTranslation from 'next-translate/useTranslation';

import {useStoreNextRouter} from '@/components/global/providers/StoreNextRouter';
import useIdentity from '@/hooks/useIdentity';
import {ORDERS_PATH} from '@/paths';
import {compilePath} from '@/utils';
import AppBarIconButton from '../AppBarIconButton';
import AppBarLink from '../AppBarLink';
import {NAVLINK_TRACKING} from '../appBarTracking';
import useExposeOnDwell from '../hooks/useExposeOnDwell';
import useFofNavbarPhase2b from '../hooks/useFofNavbarPhase2b';
import useNavbarVersion from '../hooks/useNavbarVersion';
import useNavLinkTracking from '../hooks/useNavlinkTracking';
import MenuPopover from '../Menus/MenuPopover';
import OrdersDropdown from './OrdersDropdown';

// For FOF navbar phase 2b.
// How long the pointer must rest on the order link (≥1280px screens)
// before it counts as intent and exposes.
// The treatment dropdown opens on the same delay.
export const ORDERS_EXPOSURE_DWELL_MS = 300;

const Orders: React.FC = () => {
  const {isLoggedIn} = useIdentity();
  const router = useRouter();
  const {trackLink, trackRumAction} = useNavLinkTracking();
  const {t} = useTranslation('app-bar');
  const {isOnLoggedOutLandingPage} = useStoreNextRouter();
  const {isFofNavbarPhase2bTreatment, trackExposure} = useFofNavbarPhase2b();
  const version = useNavbarVersion('orders');
  const iconButtonVersion = useNavbarVersion();
  const {startDwell, cancelDwell} = useExposeOnDwell({
    dwellMs: ORDERS_EXPOSURE_DWELL_MS,
    trackExposure,
  });

  if (!isLoggedIn) {
    return null;
  }

  const handleClick = () => {
    const destination = compilePath(ORDERS_PATH, {});
    trackLink({
      path: destination,
      title: NAVLINK_TRACKING.orders.text,
      // orders dropdown (FOF navbar phase 2b) not part of mobile UI/UX
      version: iconButtonVersion,
      datadogAction: NAVLINK_TRACKING.orders.clicked,
    });
    router.push(destination);
  };

  const handleHover = () => {
    trackRumAction({
      datadogAction: NAVLINK_TRACKING.orders.hovered,
      version,
    });
    // Expose on a dwell, the same way in every arm
    // of FOF navbar phase 2b.
    startDwell();
  };

  const handleLinkClick = () => {
    cancelDwell();
    trackExposure();
  };

  const ordersLink = (
    <AppBarLink
      onClick={handleLinkClick}
      className="px-2.5"
      label={t('orders.label')}
      path={ORDERS_PATH}
      trackingText={NAVLINK_TRACKING.orders.text}
      trackingVersion={version}
      datadogAction={NAVLINK_TRACKING.orders.clicked}
      variant={isOnLoggedOutLandingPage ? 'contrastButton' : 'secondaryButton'}
    />
  );

  return (
    <div>
      <div className="hidden appbar-xl:block" onMouseEnter={handleHover} onMouseLeave={cancelDwell}>
        {isFofNavbarPhase2bTreatment ? (
          <MenuPopover
            ariaLabel={t('orders.dropdown.label')}
            className="w-[362px]"
            content={<OrdersDropdown />}
            openDelay={ORDERS_EXPOSURE_DWELL_MS}
            placement="bottom-end"
          >
            {ordersLink}
          </MenuPopover>
        ) : (
          ordersLink
        )}
      </div>

      <div className="appbar-xl:hidden">
        <AppBarIconButton
          icon={faBowlHot}
          aria-label={t('orders.label')}
          title={t('orders.label')}
          onClick={handleClick}
          variant={isOnLoggedOutLandingPage ? 'contrast' : 'secondary'}
        />
      </div>
    </div>
  );
};

export default Orders;
